<template>
<div class="regel-benennen">

    <p class="auftrag">
        {{ uebung.zuege.length > 1 ? 'Ein Spruch in diesem Funkverkehr hat einen Fehler.' : 'Dieser Spruch hat einen Fehler.' }}
        Welche Regel ist verletzt?
        <span class="tastenhinweis">Klick oder Taste {{ 'ABCDEFGH'.slice(0, optionen.length).split('').join(', ') }}</span>
    </p>

    <AblaufKopf :uebung="uebung" />

    <div v-for="z in uebung.zuege" :key="z.nr" class="spruch" :class="{ fehlerhaft: z.nr === fehlerZug }" :style="einzug(z)">
        <p v-if="uebung.zuege.length > 1" class="station nur-vorlesen">{{ z.station.rufname }}</p>
        <p class="text">{{ textVon(z) }}</p>
        <p v-if="geloest && z.nr === fehlerZug" class="richtig">Richtig: {{ z.teile.map(t => t.text).join(' ') }}</p>
    </div>

    <div class="optionen">
        <button v-for="(o, i) in optionen" :key="o"
            :class="{ daneben: gewaehlt.includes(o) && o !== antwort, stimmt: geloest && o === antwort }"
            :disabled="geloest || gewaehlt.includes(o)"
            :aria-keyshortcuts="'ABCDEFGH'[i]"
            @click="waehlen(o)">
            <kbd aria-hidden="true">{{ 'ABCDEFGH'[i] }}</kbd>
            <span class="option__text">{{ o }}</span>
        </button>
    </div>

    <p v-if="gewaehlt.length && !geloest" class="rueckmeldung daneben">Diese Regel ist hier nicht verletzt.</p>
    <p v-if="geloest" class="rueckmeldung stimmt">Richtig.</p>

    <div class="knopfleiste">
        <slot name="rechts" />
    </div>

</div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import AblaufKopf from '@/components/AblaufKopf.vue';
import { einzugFuer } from '@/logic/Einzug';

// Uebungsart "Regel benennen": in genau einem Zug steckt ein Fehler (Ersatz
// oder Tausch). Der Zug ist markiert — gesucht ist nicht der Fehler, sondern
// die Regel, die er verletzt. Vier Regelsaetze stehen zur Wahl.
//
// Falsche Antworten sind andere Regelsaetze derselben Uebung. Ausgenommen
// ist jede Regel, die der Fehler streift ("beruehrt"): sonst stuende z.B.
// "Das Schlusswort steht am Ende" als falsch da, obwohl das Schlusswort
// durch "over" ersetzt ist.
//
// Vertrag mit dem Container wie bei jeder Uebungsart (siehe AllesSortieren).
// Jede Wahl ist ein Pruefen. Fehler und Antworten sind schon gewuerfelt:
// uebung.fehlersuche, siehe bereiteVor().
export default {
    components: { AblaufKopf },
    // Der Container reicht allen Ansichten dieselben Props; was diese nicht
    // braucht, soll nicht als HTML-Attribut am Wurzelelement landen.
    inheritAttrs: false,
    props: {
        uebung: {type: Object, required: true}
    },
    emits: ['geprueft'],
    setup(props, { emit }) {
        const { fehlerZug, fehler, antwort, optionen } = props.uebung.fehlersuche

        const textVon = (z) => (z.nr === fehlerZug ? fehler.teile : z.teile).map(t => t.text).join(' ')
        const einzug = einzugFuer(props.uebung)

        const gewaehlt = ref([])
        const geloest = computed(() => gewaehlt.value.includes(antwort))

        const waehlen = (o) => {
            gewaehlt.value.push(o)
            const sitzt = o === antwort
            emit('geprueft', { sitzt, geloest: sitzt })
        }


        // Tastenkuerzel: A, B, C … waehlen die Moeglichkeit mit diesem
        // Buchstaben. Nicht mit Strg/Alt/Cmd und nicht in Eingabefeldern.
        const BUCHSTABEN = 'abcdefgh'
        const taste = (e) => {
            if (e.ctrlKey || e.altKey || e.metaKey) return
            if (['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return
            const i = BUCHSTABEN.indexOf(e.key.toLowerCase())
            if (i === -1) return
            const o = optionen[i]
            if (geloest.value || !o || gewaehlt.value.includes(o)) return
            waehlen(o)
        }
        onMounted(() => window.addEventListener('keydown', taste))
        onUnmounted(() => window.removeEventListener('keydown', taste))

        return { fehlerZug, optionen, antwort, textVon, einzug, gewaehlt, geloest, waehlen }
    }
}
</script>

<style scoped>
.auftrag {
    margin: 0 0 1.25rem;
    font-weight: 600;
    color: var(--zs-tinte);
}

/* Station = Farbe Blau, wie ueberall: Farbe steht fuer Station oder fuer
   richtig/falsch, fuer nichts sonst. */
.station {
    margin: 0 0 0.35rem;
    font-size: 0.8rem;
    font-weight: 650;
    color: var(--zs-blau);
}

.spruch {
    margin: 0 0 1rem;
    padding: 0.7rem 0.9rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

/* Der Spruch mit dem Fehler: rot umrandet. */
.spruch.fehlerhaft {
    border-color: var(--zs-warnrot);
    box-shadow: 0 0 0 1px var(--zs-warnrot);
}

.text,
.richtig {
    margin: 0;
    line-height: 1.45;
}

.richtig {
    margin-top: 0.5rem;
    font-weight: 600;
    color: var(--zs-oliv);
}

.optionen {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 1.75rem 0 0;
}

.optionen button {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 3rem;
    padding: 0.5rem 0.85rem;
    font: inherit;
    line-height: 1.4;
    color: var(--zs-tinte);
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;
    text-align: left;
}

.optionen button:hover:not(:disabled) {
    border-color: var(--zs-tinte);
}

.optionen button:disabled {
    cursor: default;
}

.optionen button.daneben {
    color: var(--zs-warnrot);
    background: var(--zs-warnrot-hell);
    border-color: rgba(179, 38, 30, 0.45);
}

.optionen button.daneben .option__text {
    text-decoration: line-through;
}

.optionen button.stimmt {
    background: var(--zs-oliv-hell);
    border-color: var(--zs-oliv);
    box-shadow: inset 0 0 0 1px var(--zs-oliv);
}

/* Die Taste zur Moeglichkeit, wie eine Taste gezeichnet. */
kbd {
    display: inline-grid;
    place-items: center;
    flex: none;
    min-width: 1.6rem;
    height: 1.6rem;
    font-family: var(--zs-funkschrift);
    font-size: 0.8rem;
    color: var(--zs-grau);
    background: var(--zs-flaeche);
    border: 1px solid var(--zs-rahmen);
    border-bottom-width: 2px;
    border-radius: 3px;
}

.tastenhinweis {
    margin-left: 0.5rem;
    font-size: 0.85rem;
    font-weight: 400;
    color: var(--zs-grau);
}

.rueckmeldung {
    margin: 1rem 0 0;
    font-weight: 650;
}

.rueckmeldung.daneben {
    color: var(--zs-warnrot);
}

.rueckmeldung.stimmt {
    color: var(--zs-oliv);
}

.rueckmeldung.daneben::before {
    content: '\2715\00a0\00a0';
}

.rueckmeldung.stimmt::before {
    content: '\2713\00a0\00a0';
}

/* ─── Knopfleiste ────────────────────────────────────────────
   Rechts kommt, was dem Container gehoert (Neue Uebung / Weiter). */

.knopfleiste {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid var(--zs-rahmen);
}

button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Funktext in fester Laufweite: Protokolltext, nicht Bedientext. */
.text,
.richtig {
    font-family: var(--zs-funkschrift);
}

@media (prefers-reduced-motion: reduce) {
    .optionen button {
        transition: none;
    }
}
</style>
